// App-wide toast store (wiki/feature-api-client.md §"Toast system").

import { get, writable, type Readable } from 'svelte/store';

export type ToastSeverity = 'error' | 'success' | 'info';

export interface Toast {
  id: number;
  severity: ToastSeverity;
  message: string;
}

const TIMEOUT_MS: Record<ToastSeverity, number> = {
  error: 8000,
  success: 4000,
  info: 5000,
};

const internal = writable<Toast[]>([]);

export const toasts: Readable<Toast[]> = { subscribe: internal.subscribe };

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function clearTimer(id: number): void {
  const handle = timers.get(id);
  if (handle !== undefined) {
    clearTimeout(handle);
    timers.delete(id);
  }
}

// A burst of identical failures shows one toast; a repeat only extends its life.
function push(severity: ToastSeverity, message: string, sticky = false): number {
  const shown = get(internal).find((t) => t.severity === severity && t.message === message);
  const id = shown?.id ?? nextId++;
  if (shown) {
    if (!timers.has(id)) return id;
    clearTimer(id);
  } else {
    internal.update((list) => [...list, { id, severity, message }]);
  }

  if (!sticky && typeof setTimeout === 'function') {
    const handle = setTimeout(() => dismiss(id), TIMEOUT_MS[severity]);
    timers.set(id, handle);
  }
  return id;
}

export function dismiss(id: number): void {
  clearTimer(id);
  internal.update((list) => list.filter((t) => t.id !== id));
}

export const toast = {
  error: (message: string): number => push('error', message),
  success: (message: string): number => push('success', message),
  info: (message: string): number => push('info', message),
  sticky: (message: string): number => push('info', message, true),
  dismiss,
};

export function __resetToastsForTests(): void {
  for (const id of timers.keys()) clearTimer(id);
  nextId = 1;
  internal.set([]);
}
